import { useState } from "react";
import type { CarrierDto } from "@babackapp/private-api-client/types";
import { fetchClient } from "@babackapp/private-api-client/generated/client";
import useTranslation from "@babackapp/translations/use-translation";
import { Button } from "@babackapp/ui/components/button";
import { Input } from "@babackapp/ui/components/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@babackapp/ui/components/select";
import { useCarriersContext } from "../../use-carriers-context";
import {
  FEDEX_PICKUP_DROPOFF,
  FEDEX_PICKUP_SCHEDULED,
  serviceLaneFor,
} from "../fedex-plans";
import { RatesTable, type RateDetail } from "../forms/fedex-rates-display";

type RateResponse = {
  transactionId?: string;
  output?: { rateReplyDetails?: RateDetail[] };
  errors?: { code: string; message: string }[];
};

type PickupResult = {
  pickupType: string;
  labelKey: "pickupScheduled" | "pickupDropoff";
  data?: RateResponse;
  error?: string;
};

const PICKUPS = [
  { pickupType: FEDEX_PICKUP_SCHEDULED, labelKey: "pickupScheduled" as const },
  { pickupType: FEDEX_PICKUP_DROPOFF, labelKey: "pickupDropoff" as const },
];

function extractApiMessage(m: unknown): string | undefined {
  if (typeof m === "string") return m;
  if (Array.isArray(m)) {
    const parts = m
      .map((x) =>
        typeof x === "string"
          ? x
          : x && typeof x === "object" && "message" in x
            ? String((x as { message: unknown }).message)
            : "",
      )
      .filter(Boolean);
    return parts.length ? parts.join(", ") : undefined;
  }
  return undefined;
}

// Lowest quoted total for a pickup result, used to flag the cheaper option.
// Uses the ACCOUNT (negotiated) net so it matches the price shown in the table.
function minTotal(data?: RateResponse): number | undefined {
  const totals = (data?.output?.rateReplyDetails ?? [])
    .map((r) => {
      const details = r.ratedShipmentDetails ?? [];
      const account =
        details.find((d) => d.rateType?.includes("ACCOUNT")) ?? details[0];
      return account?.totalNetCharge;
    })
    .filter((n): n is number => typeof n === "number");
  return totals.length ? Math.min(...totals) : undefined;
}

// Self-service FedEx return-rate viewer, embedded inline in the carrier edit
// modal so it always reflects the merchant's currently-selected product. Origin
// = the customer's country (where the return ships from); destination = the
// shop's return warehouse (fixed). The service type comes from the selected
// product, and the origin lane is constrained to match it: an international
// product excludes the warehouse country from origins, a domestic product locks
// the origin to the warehouse country, and One Rate sends no explicit service
// (FedEx returns all applicable rates). This keeps requests lane-compatible so
// FedEx never rejects with SERVICETYPE.NOT.ALLOWED. Since this is a returns
// product there is no DDP/DAP choice. Fires one rate call per pickup type so the
// merchant can compare Scheduled pickup vs Drop-off.
export function FedexRatesPanel({
  carrier,
  selectedServiceValue,
}: {
  carrier: CarrierDto;
  selectedServiceValue: string;
}) {
  const t = useTranslation();
  const { shop } = useCarriersContext();
  const shopId = shop.id;

  const defaultLocation =
    shop.locations.find((l) => l.isDefault) ?? shop.locations[0];
  const destinationCountry =
    defaultLocation?.address?.country_code ??
    carrier.destinationAccepted[0] ??
    "FR";
  const destinationPostal = defaultLocation?.address?.zip ?? "";

  const lane = serviceLaneFor(selectedServiceValue);
  const lockOrigin = lane !== "international";
  // International: every accepted origin except the warehouse (cross-border).
  const intlOriginOptions = carrier.originAccepted.filter(
    (c) => c !== destinationCountry,
  );

  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState<PickupResult[] | null>(null);
  const [originCountry, setOriginCountry] = useState(
    () => intlOriginOptions[0] ?? destinationCountry,
  );
  const [originPostal, setOriginPostal] = useState("");
  const [weightValue, setWeightValue] = useState("1");
  const [weightUnit, setWeightUnit] = useState<"KG" | "LB">("KG");

  // Effective origin used for the query and the select value. When the lane is
  // locked (domestic / One Rate) it is the warehouse country; for international
  // it is the picked value, falling back to the first compatible option if the
  // current selection is no longer valid (e.g. product just changed).
  const originValue = lockOrigin
    ? destinationCountry
    : intlOriginOptions.includes(originCountry)
      ? originCountry
      : intlOriginOptions[0];

  // Never send a serviceType: FedEx then returns every product applicable to the
  // lane (origin/destination decide the lane), so the merchant compares all the
  // related products at once instead of a single one.
  const noCompatibleOrigin = !lockOrigin && !originValue;

  const fetchForPickup = async (
    pickup: (typeof PICKUPS)[number],
  ): Promise<PickupResult> => {
    try {
      const body: Record<string, unknown> = {
        carrierId: carrier.id,
        origin: {
          postalCode: originPostal,
          countryCode: originValue,
        },
        destination: {
          postalCode: destinationPostal,
          countryCode: destinationCountry,
        },
        weight: {
          value: Number(weightValue) || 1,
          units: weightUnit,
        },
        pickupType: pickup.pickupType,
      };

      const {
        data: res,
        error: err,
        response,
      } = await fetchClient.POST(
        `/shops/${shopId}/carriers/fedex/rates` as never,
        { body } as never,
      );
      if (err || !response.ok) {
        const e = err as
          | { message?: unknown; errors?: { message?: string }[] }
          | undefined;
        const msg = extractApiMessage(e?.message) ?? e?.errors?.[0]?.message;
        throw new Error(msg || `HTTP ${response.status}`);
      }
      return { ...pickup, data: res as RateResponse };
    } catch (e) {
      return { ...pickup, error: (e as Error).message };
    }
  };

  const handleGetRates = async () => {
    setLoading(true);
    setResults(null);
    const next = await Promise.all(PICKUPS.map(fetchForPickup));
    setResults(next);
    setLoading(false);
  };

  const cheaperPickupType = (() => {
    if (!results) return undefined;
    const withTotals = results
      .map((r) => ({ pickupType: r.pickupType, total: minTotal(r.data) }))
      .filter(
        (r): r is { pickupType: string; total: number } =>
          typeof r.total === "number",
      );
    if (withTotals.length < 2) return undefined;
    return withTotals.reduce((a, b) => (b.total < a.total ? b : a)).pickupType;
  })();

  return (
    <div className="space-y-3 border-t border-gray-200 pt-3">
      <Button
        variant="outline"
        size="sm"
        type="button"
        onClick={() => setOpen((v) => !v)}
      >
        {open
          ? t("carrier.fedexCsp.hideRates")
          : t("carrier.fedexCsp.checkRates")}
      </Button>

      {open && (
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <label className="block text-sm font-medium text-gray-700">
                {t("carrier.fedexCsp.origin")}
              </label>
              {lockOrigin ? (
                <Input value={originValue ?? ""} readOnly disabled />
              ) : (
                <Select
                  value={originValue}
                  onValueChange={setOriginCountry}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {intlOriginOptions.map((c) => (
                      <SelectItem key={c} value={c}>
                        {c}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
            <div className="space-y-1.5">
              <label className="block text-sm font-medium text-gray-700">
                {t("carrier.fedexCsp.postalCode")}
              </label>
              <Input
                value={originPostal}
                onChange={(e) => setOriginPostal(e.target.value)}
                placeholder="75001"
              />
            </div>
            <div className="space-y-1.5">
              <label className="block text-sm font-medium text-gray-700">
                {t("carrier.fedexCsp.destination")}
              </label>
              <Input
                value={`${destinationCountry} ${destinationPostal}`.trim()}
                readOnly
                disabled
              />
            </div>
            <div className="space-y-1.5">
              <label className="block text-sm font-medium text-gray-700">
                {t("carrier.fedexCsp.weight")}
              </label>
              <div className="flex gap-2">
                <Input
                  type="number"
                  min="0"
                  step="0.1"
                  value={weightValue}
                  onChange={(e) => setWeightValue(e.target.value)}
                />
                <Select
                  value={weightUnit}
                  onValueChange={(v) => setWeightUnit(v as "KG" | "LB")}
                >
                  <SelectTrigger className="w-24">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="KG">KG</SelectItem>
                    <SelectItem value="LB">LB</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          <p className="text-xs text-gray-500">
            {lane === "international"
              ? t("carrier.fedexCsp.allRatesInternational")
              : t("carrier.fedexCsp.allRatesDomestic")}
          </p>

          {noCompatibleOrigin ? (
            <p className="text-sm text-gray-500">
              {t("carrier.fedexCsp.noCompatibleOrigin")}
            </p>
          ) : (
            <Button
              size="sm"
              type="button"
              disabled={loading}
              onClick={handleGetRates}
            >
              {t("carrier.fedexCsp.getRates")}
            </Button>
          )}

          {loading && (
            <p className="text-sm text-gray-500">
              {t("carrier.fedexCsp.ratesLoading")}
            </p>
          )}

          {results && !loading && (
            <div className="space-y-4">
              {results.map((r) => {
                const apiError = r.data?.errors?.[0];
                return (
                  <div key={r.pickupType} className="space-y-2">
                    <div className="flex items-center gap-2">
                      <h4 className="text-sm font-semibold text-gray-800">
                        {t(`carrier.fedexCsp.${r.labelKey}`)}
                      </h4>
                      {cheaperPickupType === r.pickupType && (
                        <span className="inline-flex items-center rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800">
                          {t("carrier.fedexCsp.cheaper")}
                        </span>
                      )}
                    </div>
                    {r.error && (
                      <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded text-sm">
                        {r.error}
                      </div>
                    )}
                    {apiError && (
                      <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded text-sm">
                        {apiError.code}: {apiError.message}
                      </div>
                    )}
                    {r.data && !apiError && (
                      <RatesTable
                        rates={r.data.output?.rateReplyDetails ?? []}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
